import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { Notice, PageHeader } from '@wodi/ui';
import { useRef, useState, type ReactNode } from 'react';

import { can, sessionQuery } from '../api/session.ts';
import homeStyles from '../home/home.module.css';
import { TodayPanel } from '../home/today-visits.tsx';
import { CheckInPanel } from '../patients/check-in-panel.tsx';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { PatientSearchPicker } from '../patients/patient-search-palette.tsx';
import styles from '../patients/patients.module.css';
import { toasts } from '../toasts.ts';
import { screen } from '../layout/screen.tsx';
import { AppointmentCheckIn } from '../appointments/check-in-note.tsx';

const route = getRouteApi('/_app/reception');

/**
 * Reception (plan §6.3, DESIGN.md §8): search first. Choosing a patient shows the banner
 * and the check-in panel; after check-in, focus goes back to the search field for the
 * next person. The chosen patient's ID (a UUID, never a name or number) is in the
 * address so the page survives a refresh; the search text is not. Beside it, today's
 * arrivals at this facility (docs/plans/phase1-home-and-demo-data.md §3).
 */
export function ReceptionPage({
  registerAction,
}: {
  /** "Register new patient", shown once a search finds nobody. */
  registerAction?: (text: string) => ReactNode;
}) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const { patientId, appointmentId } = route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const [appointment, setAppointment] = useState<{ id: string; servicePointId: string } | null>(
    null,
  );

  if (session === null || !can(session, 'visits.open')) {
    return (
      <>
        <PageHeader title="Reception" />
        <Notice tone="info">
          You don't have permission to check patients in. Ask an administrator if you need it.
        </Notice>
      </>
    );
  }

  const choose = (id: string | undefined) => {
    void navigate({ to: '/reception', search: id === undefined ? {} : { patientId: id } });
  };

  const seesToday = can(session, 'visits.read') && can(session, 'patients.read');

  return (
    <div className={styles.page}>
      <PageHeader title="Reception" />
      <div className={seesToday ? homeStyles.reception : undefined}>
        <div className={homeStyles.receptionMain}>
          <section className={screen.panel} aria-label="Find a patient">
            <PatientSearchPicker
              autoFocus
              inputRef={searchRef}
              inputValue={text}
              onInputChange={setText}
              onSelect={(result) => {
                setText('');
                choose(result.id);
              }}
              {...(registerAction === undefined ? {} : { emptyAction: registerAction })}
            />
          </section>
          {patientId === undefined ? null : (
            <div className={screen.page}>
              <PatientBannerLoader patientId={patientId} />
              {appointmentId === undefined ? null : (
                <AppointmentCheckIn
                  patientId={patientId}
                  appointmentId={appointmentId}
                  onFound={setAppointment}
                />
              )}
              <CheckInPanel
                key={`${patientId}:${appointment?.id ?? ''}`}
                patientId={patientId}
                {...(appointment === null || appointment.id !== appointmentId
                  ? {}
                  : { appointment })}
                canCheckSha={can(session, 'sha.eligibility.check')}
                onCheckedIn={(visit) => {
                  toasts.show({
                    title: 'Patient checked in',
                    // The ticket is what the waiting-area display shows (triage plan P1, P8).
                    description: `${
                      visit.queueEntry === null
                        ? `Visit ${visit.visitNumber}.`
                        : `Visit ${visit.visitNumber}, waiting at ${visit.queueEntry.servicePoint.name}.`
                    }${visit.queueTicket === null ? '' : ` Ticket ${visit.queueTicket}.`}`,
                  });
                  void queryClient.invalidateQueries({ queryKey: ['visits', 'today'] });
                  choose(undefined);
                  searchRef.current?.focus();
                }}
              />
            </div>
          )}
        </div>
        {seesToday ? <TodayPanel /> : null}
      </div>
    </div>
  );
}
